import { useState } from "react";
import axios from "axios";
import { COMPANY } from "@/data";
import { Mail, MapPin, Phone, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState({ type: null, msg: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ type: null, msg: "" });
    if (!form.name || !form.email || !form.message) {
      setStatus({ type: "error", msg: "Please fill in name, email, and message." });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      setStatus({ type: "success", msg: res.data.message });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setStatus({
        type: "error",
        msg: err?.response?.data?.detail || "Something went wrong. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16">
          <div className="col-span-12 lg:col-span-7">
            <div className="hu-section-tag mb-6">Get in touch</div>
            <h1 className="font-display font-bold text-5xl lg:text-7xl tracking-tight leading-[0.95] text-neutral-900">
              Talk to a person<br />who knows your job.
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:pt-12">
            <p className="text-neutral-600 leading-relaxed text-base lg:text-lg">
              For quotes, technical questions on engineered components, or general
              inquiries — drop us a line and we'll get right back to you.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-0 border border-stone-200 rounded-sm overflow-hidden">
          {/* Contact info */}
          <aside className="col-span-12 lg:col-span-5 bg-stone-100 p-8 lg:p-12">
            <div className="hu-section-tag mb-6">Direct contacts</div>
            <ul className="space-y-6">
              {COMPANY.team.map((m) => (
                <li key={m.name} data-testid={`contact-team-${m.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="font-display font-bold text-2xl text-neutral-900 tracking-tight">{m.name}</div>
                  <div className="text-xs uppercase tracking-widest text-amber-700 mt-1">{m.role}</div>
                  <a
                    href={`mailto:${m.email}`}
                    className="block text-sm text-neutral-700 mt-2 hover:text-amber-700"
                  >
                    <Mail size={14} className="inline mr-2" />
                    {m.email}
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-12 pt-8 border-t border-stone-300">
              <div className="hu-section-tag mb-4">Phone</div>
              <a
                href={COMPANY.phoneHref}
                data-testid="contact-phone-link"
                className="inline-flex items-center gap-2 font-display font-bold text-2xl tracking-tight text-neutral-900 hover:text-amber-700 hu-ticker"
              >
                <Phone size={20} className="text-amber-700" />
                {COMPANY.phone}
              </a>
            </div>

            <div className="mt-12 pt-8 border-t border-stone-300">
              <div className="hu-section-tag mb-4">Service Area</div>
              <ul className="space-y-2">
                {COMPANY.serviceAreas.map((a) => (
                  <li key={a} className="flex items-center gap-2 text-sm text-neutral-700">
                    <MapPin size={14} className="text-amber-700" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Form */}
          <div className="col-span-12 lg:col-span-7 bg-white p-8 lg:p-12">
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Your Name *">
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    data-testid="contact-input-name"
                    className="hu-input"
                  />
                </Field>
                <Field label="Email *">
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid="contact-input-email"
                    className="hu-input"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    data-testid="contact-input-phone"
                    className="hu-input"
                  />
                </Field>
                <Field label="Subject">
                  <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => update("subject", e.target.value)}
                    data-testid="contact-input-subject"
                    className="hu-input"
                  />
                </Field>
              </div>

              <Field label="Message *">
                <textarea
                  required
                  rows={7}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  data-testid="contact-input-message"
                  className="hu-input"
                />
              </Field>

              {status.type && (
                <div
                  data-testid={`contact-status-${status.type}`}
                  className={`flex items-start gap-3 p-4 border rounded-sm text-sm ${
                    status.type === "success"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                      : "bg-red-50 border-red-200 text-red-900"
                  }`}
                >
                  {status.type === "success" ? (
                    <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle size={18} className="shrink-0 mt-0.5" />
                  )}
                  <p>{status.msg}</p>
                </div>
              )}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-800 disabled:bg-stone-400 text-white text-sm font-medium px-6 py-3.5 rounded-sm transition-colors min-w-[160px]"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>Send Message</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .hu-input {
          width: 100%;
          background: #fff;
          border: 1px solid #D6D3CD;
          border-radius: 2px;
          padding: 10px 12px;
          font-size: 14px;
          color: #1C2023;
          font-family: 'IBM Plex Sans', sans-serif;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .hu-input:focus {
          outline: none;
          border-color: #C18341;
          box-shadow: 0 0 0 3px rgba(193, 131, 65, 0.18);
        }
        .hu-input::placeholder { color: #9CA39B; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-neutral-700 uppercase tracking-wider mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
